import { Fragment, FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { Col, Row } from "antd";

import routesStore from "../../../store/routesSettings";
import {
  FormBlock,
  FormField,
  IButton,
  ICheckbox,
  ISelect,
  ITooltip,
} from "../../../ui-kit";
import { useHandleForm } from "../../../hooks/useHandleForm";
import { useStatusMessage } from "../../../hooks/useStatusMessage";
import { Controller, useFieldArray } from "react-hook-form";
import userStore from "../../../store/users";
import { TrashIco } from "../../../assets/customIcons";
import { string, object, array } from "yup";

import styles from "../styles.module.scss";
import Item from "antd/lib/list/Item";
import Constants from "../../../constants/Common";

function emptyStringToNull(value, originalValue) {
  if (typeof originalValue === "number") {
    return originalValue.toString();
  }
  return value;
}

const stepSchema = {
  userId: string().matches(/^(\s*|\d+)$/),
  userGroupId: string()
    .transform(emptyStringToNull)
    .matches(/^\d+$/)
    .required(),
  newStatus: string().required(),
  newStatusReject: string().required(),
};

const schema = object().shape({
  entityName: string().required(),
  steps: array().of(object().shape(stepSchema)).min(1),
});

const Route: FC = observer((values) => {
  const {
    titleIcons: { trash },
  } = Constants;
  const { usersData, roles } = userStore;

  const { createRoute, updateRoute } = routesStore;
  const initialFocus = {};
  const initialValues = {
    ...(values?.record || {}),
  };
  initialValues.steps = initialValues.steps?.map((step) => ({
    ...step,
    userId: step.userId?.toString(),
    userGroupId: step.userGroupId?.toString(),
  }));

  const {
    setValue,
    register,
    handleSubmit,
    watchAllField,
    isFocused,
    handleBlur,
    handleFocus,
    control,
    formState: { isValid },
    reset,
  } = useHandleForm(schema, initialValues, initialFocus, "all");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "steps",
  });

  fields.sort((a, b) => a.orderNum - b.orderNum);

  const onSubmit = (values) => {
    if (values.id) {
      updateRoute(values);
    } else {
      createRoute(values);
      reset();
    }
  };

  const toggleBoolean = (index, key) => {
    setValue(`steps.${index}.${key}`, !watchAllField.steps[index][key]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormBlock>
          <Row gutter={[15, 20]}>
            <Col span={6}>
              <FormField
                register={register}
                label="Форма/документ"
                name="entityName"
                type="text"
                currentValue={watchAllField.entityName}
                isRequired
                disabled={true}
              />
            </Col>
            <Col span={6}>
              <FormField
                register={register}
                label="Название"
                name="name"
                type="text"
                currentValue={watchAllField.name}
              />
            </Col>
            <Col>
              <ICheckbox
                onChange={() =>
                  setValue(
                    "completeByOrganizationStarted",
                    !watchAllField.completeByOrganizationStarted,
                  )
                }
                label="Маршрут может закрыть только организация-инициатор"
                checked={watchAllField.completeByOrganizationStarted}
              />
            </Col>
          </Row>
          <h3>Маршрут</h3>
          {fields.map((item, index) => (
            <Fragment key={item.id}>
              <Row gutter={[15, 20]} align={"middle"}>
                <Col>
                  {/* <h4>Шаг {item.orderNum + 1}</h4> */}
                  <h4>Шаг {index + 1}</h4>
                </Col>
                <Col>
                  <ITooltip title={trash} placement="right">
                    <div className={styles["TrashIco"]}>
                      <TrashIco onClick={() => remove(index)} />
                    </div>
                  </ITooltip>
                </Col>
              </Row>

              <Row gutter={[15, 20]}>
                <Col span={4}>
                  <Controller
                    name={`steps.${index}.userGroupId`}
                    control={control}
                    render={({ field }) => (
                      <ISelect
                        field={field}
                        optionsArr={roles.map((item) => ({
                          ...item,
                          id: item.id.toString(),
                        }))}
                        placeholder="Роль"
                        isRequired
                      />
                    )}
                  />
                </Col>
                <Col span={4}>
                  <Controller
                    name={`steps.${index}.userId`}
                    control={control}
                    render={({ field }) => (
                      <ISelect
                        field={field}
                        optionsArr={usersData
                          .filter((user) =>
                            watchAllField.steps[index].userGroupId
                              ? user.userGroupId ===
                                +watchAllField.steps[index].userGroupId
                              : true,
                          )
                          .map((item) => ({
                            ...item,
                            id: item.userId.toString(),
                            name: item.fullName,
                          }))}
                        placeholder="Пользователь"
                      />
                    )}
                  />
                </Col>
              </Row>
              <Row gutter={[15, 20]}>
                <Col span={4}>
                  <FormField
                    type="text"
                    register={register}
                    label="Статус при согласовании"
                    name={`steps.${index}.newStatus`}
                    currentValue={watchAllField.steps[index].newStatus}
                  />
                </Col>
                <Col span={5}>
                  <ICheckbox
                    onChange={() =>
                      toggleBoolean(index, "commentConfirmRequired")
                    }
                    label="Обязательное примечание при согласовании"
                    checked={watchAllField.steps[index].commentConfirmRequired}
                  />
                </Col>
                <Col span={5}>
                  <ICheckbox
                    onChange={() => toggleBoolean(index, "fileConfirmRequired")}
                    label="Обязательно вложение при согласовании"
                    checked={watchAllField.steps[index].fileConfirmRequired}
                  />
                </Col>
              </Row>
              <Row gutter={[15, 20]}>
                <Col span={4}>
                  <FormField
                    type="text"
                    register={register}
                    label="Статус при отклонении"
                    name={`steps.${index}.newStatusReject`}
                    currentValue={watchAllField.steps[index].newStatusReject}
                  />
                </Col>
                <Col span={5}>
                  <ICheckbox
                    onChange={() =>
                      toggleBoolean(index, "commentRejectRequired")
                    }
                    label="Обязательное примечание при отклонении"
                    checked={watchAllField.steps[index].commentRejectRequired}
                  />
                </Col>
                <Col span={5}>
                  <ICheckbox
                    onChange={() => toggleBoolean(index, "fileRejectRequired")}
                    label="Обязательно вложение при отклонении"
                    checked={watchAllField.steps[index].fileRejectRequired}
                  />
                </Col>
              </Row>
            </Fragment>
          ))}
          <Row gutter={[15, 20]}>
            <Col span={2}>
              <IButton type="submit" disabled={!isValid}>
                Сохранить
              </IButton>
            </Col>
            <Col span={2}>
              <IButton
                onClick={(e) => {
                  e.preventDefault();
                  append({
                    userGroupId: "",
                    userId: "",
                    orderNum: (fields[fields.length - 1]?.orderNum || 0) + 1,
                  });
                }}
              >
                Добавить шаг
              </IButton>
            </Col>
          </Row>
        </FormBlock>
      </form>
    </>
  );
});

export default Route;
